import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, map, retry, switchMap } from 'rxjs/operators';
import { AcModel } from '../../../models/ac.model';
import { ApiService } from '../../../services/api.service';
import { DataService } from '../../../services/data.service';
import { UiService } from '../../../services/ui.service';
import { CandidateModel } from '../../../models/candidate.model';
import { AuthStateService } from '../../../services/auth-state.service';
import * as CryptoJS from 'crypto-js';

const POLLING_INTERVAL_STANDARD = 5;
type PayloadType = { [key: string]: string } | null;

@Component({
  selector: 'app-notices',
  templateUrl: './notices.component.html',
  styleUrls: ['./notices.component.scss'],
})
export class NoticesComponent implements OnInit, OnDestroy {

  private polling: Subscription = null;
  private lastHash: string = null;

  public ac: AcModel = null;
  public candidate: CandidateModel = null;
  public form: UntypedFormGroup = this.fb.group({});

  constructor(
    private data: DataService,
    private fb: UntypedFormBuilder,
    private api: ApiService,
    private ui: UiService,
    private transloco: TranslocoService,
    public auth: AuthStateService
  ) {}

  ngOnInit(): void {
    this.ui.setPageTitle(this.transloco.translate('common.labels.notices'));
    this.data.ac$.subscribe({
      next: (ac: AcModel) => {
        if (ac !== null) {
          this.ac = ac;
          this.initForm();
          this.changeCandidate();
          this.data.setActiveNav('notices');
        }
      }
    });
    this.polling = timer(0, POLLING_INTERVAL_STANDARD * 1000)
      .pipe(
        // irgendwann verbessern per HostListener (keyup) und pipe
        switchMap(() => {
          if (this.ac && !this.ac.isFinished()) {
            const payload = this.getPayload();
            if (this.hasToSave(payload)) {
              return this.api.setNotice(payload).pipe(
                catchError(() => {
                  return EMPTY;
                })
              );
            } else {
              return EMPTY;
            }
          } else {
            return EMPTY;
          }
        })
      )
      .subscribe(() => {
        const payload = this.getPayload();
        this.lastHash = this.getHash(payload);
        this.saveNotices(payload);
      });
  }

  ngOnDestroy(): void {
    if (this.polling !== null) {
      const payload = this.getPayload();
      if (this.hasToSave(payload)) {
        this.api.setNotice(payload).subscribe({
          next: () => {
            this.saveNotices(payload);
          }
        });
      }
      this.polling.unsubscribe();
      this.polling = null;
    }
  }

  public initForm(): void {
    this.form.addControl('general', new UntypedFormControl(this.ac.notices['general'] || ''));
    this.ac?.candidates?.forEach((candidate: CandidateModel) => {
      this.form.addControl(candidate.hashed_id, new UntypedFormControl(this.ac.notices[candidate.hashed_id] || ''));
    });
    this.lastHash = this.getHash(this.getPayload());
  }

  public getPayload(): PayloadType | null {
    const payload: PayloadType = {};
    if (this.form.get('general')?.value) {
      payload.general = this.form.get('general').value;
    }
    if (!this.auth.isCandidate()) {
      this.ac?.candidates?.forEach((candidate: CandidateModel) => {
        if (this.form.get(candidate.hashed_id)?.value) {
          payload[candidate.hashed_id] = this.form.get(candidate.hashed_id).value;
        } else {
          payload[candidate.hashed_id] = null;
        }
      });
    }
    return (Object.keys(payload).length > 0) ? payload : null;
  }

  public hasToSave(payload: PayloadType): boolean {
    return this.lastHash !== null && payload !== null && this.lastHash !== this.getHash(payload);
  }

  private getHash(payload: PayloadType): string {
    const hash = CryptoJS.MD5(JSON.stringify(payload)).toString();
    return hash;
  }

  public getEditorInit(): any {
    let language = 'en';
    let language_url = null;
    if (this.transloco.getActiveLang() === 'de') {
      language = 'de';
      language_url = 'assets/tiny/langs/de.js';
    }
    return {
      height: 'calc(100% - 20px)',
      menubar: false,
      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview', 'anchor',
        'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
      ],
      toolbar: 'undo redo | blocks | bold italic backcolor | \
        alignleft aligncenter alignright alignjustify | \
        bullist numlist outdent indent | removeformat',
      language,
      language_url
    };
  }

  public changeCandidate(candidate: CandidateModel = null): void {
    if (this.auth.isCandidate()) {
      return null;
    }
    this.candidate = candidate || this.ac?.candidates[0] || null;
  }


  private saveNotices(payload: PayloadType): void {
    if (this.ac) {
      this.ac.notices = payload;
    }
  }
}
